import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { Toast } from "src/components";
import {
    getSelectedCustomBackgroundUrl,
    normalizeBackgroundEffect
} from "src/scenes/Chat/components/call/ChatCallPanel.constants";
import {
    applyVirtualBackgroundToStream,
    createVirtualBackgroundVideoTrack
} from "src/scenes/Chat/components/call/virtualBackgroundProcessor";
import {
    MatrixRtcLiveKitSession,
    waitForMatrixRtcRoom
} from "src/scenes/Chat/services/MatrixRtcLiveKitSession";
import { UserKeys } from "src/constants/userDetails";
import {
    getMeeting,
    getMeetingOccurrence,
    joinInternalMeeting,
    joinInternalMeetingOccurrence,
    leaveInternalMeeting,
    leaveInternalMeetingOccurrence
} from "src/api/meetingsApi";
import {
    createMeetingEventMetadata,
    createSharedCallSummary,
    getLatestMembershipExpiry,
    getMeetingRecipientIds,
    MEETING_EVENT_METADATA_FIELD,
    MEETING_RING_LIFETIME_MS,
    SHARED_CALL_SUMMARY_FIELD,
    WEB_CALL_SUMMARY_FIELD
} from "src/scenes/Chat/utils/meetingCallEventContract";
import { SCREEN_SHARE_FAILURE_MESSAGE } from "src/scenes/Chat/services/ScreenShareHandover";
import { sendMeetingNotifications } from "src/scenes/Chat/utils/meetingCallNotifications";

const CALL_STATES = {
    IDLE: "idle",
    OUTBOUND: "outbound",
    INBOUND: "inbound",
    ON_CALL: "on_call"
};
const VIDEO_CONSTRAINTS = {
    width: { ideal: 640, max: 640 },
    height: { ideal: 360, max: 360 },
    frameRate: { ideal: 15, max: 20 }
};
const PLACEHOLDER_WIDTH = 640;
const PLACEHOLDER_HEIGHT = 360;
const CAMERA_START_ERROR_MESSAGE = "The video feed could not be started. Check camera permissions or device availability.";

const normalizeDeviceId = deviceId => {
    if (!deviceId || deviceId === "default" || String(deviceId).startsWith("active-preview")) {
        return "";
    }
    return deviceId;
};

const makeCallId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const isFirefox = () => /firefox/i.test(window.navigator?.userAgent || "");

const getInitials = (name = "") => name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join("")
    .toUpperCase() || "ME";

export const meetingMatchesCallDescriptor = (meeting, descriptor) => (
    meeting?.meetingUuid === descriptor?.meetingUuid &&
    (meeting?.chatUuid || meeting?.meetingUuid) === descriptor?.chat?.uuid &&
    (!descriptor?.occurrenceUuid || meeting?.occurrence?.occurrenceUuid === descriptor.occurrenceUuid) &&
    meeting?.matrixRoomId === descriptor?.chat?.matrixRoomId
);

const createPlaceholderVideoTrack = (initials = "ME") => {
    const canvas = document.createElement("canvas");
    canvas.width = PLACEHOLDER_WIDTH;
    canvas.height = PLACEHOLDER_HEIGHT;
    const context = canvas.getContext("2d");
    context.fillStyle = "#111827";
    context.fillRect(0, 0, canvas.width, canvas.height);
    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, "#0B66E4");
    gradient.addColorStop(1, "#7C3AED");
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(canvas.width / 2, canvas.height / 2, 70, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#ffffff";
    context.font = "800 42px sans-serif";
    context.textAlign = "center";
    context.fillText(initials, canvas.width / 2, canvas.height / 2 + 14);

    const [track] = canvas.captureStream(5).getVideoTracks();
    track.__meBusinessPlaceholder = true;
    return track;
};

const stopTrack = track => {
    if (track?.__meBusinessVirtualBackgroundCleanup) {
        track.__meBusinessVirtualBackgroundCleanup();
        return;
    }
    track?.stop?.();
};

export const useCallWindowRuntime = ({ bridge, callToken, descriptor }) => {
    const matrixClient = bridge.matrixClient;
    const matrixRtcSessionRef = useRef(null);
    const localStreamRef = useRef(null);
    const placeholderVideoTrackRef = useRef(null);
    const retainedFirefoxVideoTrackRef = useRef(null);
    const screenShareTrackRef = useRef(null);
    const screenShareStopInProgressRef = useRef(false);
    const screenShareStartInProgressRef = useRef(false);
    const participantIdsRef = useRef(new Set([
        matrixClient?.getUserId?.(),
        descriptor.sender
    ].filter(Boolean)));
    const closingRef = useRef(false);
    const hasNotesRef = useRef(descriptor.type === "outbound" ? false : descriptor.hasNotes);
    const mediaPreferencesRef = useRef({
        microphoneEnabled: true,
        cameraEnabled: true,
        microphoneDeviceId: "",
        cameraDeviceId: "",
        speakerDeviceId: "",
        backgroundEffect: "none",
        customBackgroundUrl: ""
    });
    const initialCallId = descriptor.callId || makeCallId();
    const isMeetingSummaryPublisher = descriptor.chat?.meetingCanManage === true
        || descriptor.chat?.meetingHostEmployeeUuid === bridge.currentUser?.[UserKeys.UUID]
        || descriptor.chat?.meetingHostEmployeeUuid === bridge.currentUser?.[UserKeys.EMPLOYEE_UUID]
        || descriptor.type === "outbound";
    const [callState, setCallState] = useState({
        status: descriptor.type === "inbound" ? CALL_STATES.INBOUND : CALL_STATES.IDLE,
        callId: initialCallId,
        callIds: [initialCallId],
        direction: descriptor.type,
        sender: descriptor.sender || matrixClient?.getUserId?.() || "",
        eventId: descriptor.eventId || "",
        isGroupCall: Boolean(descriptor.chat?.isGroupChat),
        groupCallId: initialCallId,
        matrixRoomId: descriptor.chat?.matrixRoomId || "",
        chatUuid: descriptor.chat?.uuid || "",
        startedAt: 0,
        suppressRingtone: descriptor.suppressRingtone === true,
        ...(typeof hasNotesRef.current === "boolean" ? { hasNotes: hasNotesRef.current } : {})
    });
    const callStateRef = useRef(callState);
    const [localStream, setLocalStream] = useState(null);
    const [localScreenShareStream, setLocalScreenShareStream] = useState(null);
    const [remoteStreams, setRemoteStreams] = useState([]);
    const [screenShareIsActive, setScreenShareIsActive] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);

    const updateCallState = useCallback((nextStateOrUpdater) => {
        const nextState = typeof nextStateOrUpdater === "function"
            ? nextStateOrUpdater(callStateRef.current)
            : nextStateOrUpdater;
        callStateRef.current = nextState;
        setCallState(nextState);
        bridge.reportStatus?.({
            ...nextState,
            participantUserIds: Array.from(participantIdsRef.current)
        });
        return nextState;
    }, [bridge]);

    const markCallHasNotes = useCallback(() => {
        hasNotesRef.current = true;
        updateCallState(previous => ({ ...previous, hasNotes: true }));
    }, [updateCallState]);

    const currentUserInitials = useCallback(() => {
        const currentUserId = matrixClient?.getUserId?.();
        const currentChatUser = descriptor.chat?.users?.find(user => user.matrixUserId === currentUserId);
        const firstName = currentChatUser?.employeeDetails?.firstName || currentChatUser?.firstName || "";
        const lastName = currentChatUser?.employeeDetails?.lastName || currentChatUser?.lastName || "";
        return getInitials(`${firstName} ${lastName}`.trim());
    }, [descriptor.chat?.users, matrixClient]);

    const setMediaPreferences = useCallback(preferences => {
        mediaPreferencesRef.current = {
            ...mediaPreferencesRef.current,
            ...preferences,
            microphoneDeviceId: normalizeDeviceId(preferences.microphoneDeviceId ?? mediaPreferencesRef.current.microphoneDeviceId),
            cameraDeviceId: normalizeDeviceId(preferences.cameraDeviceId ?? mediaPreferencesRef.current.cameraDeviceId),
            backgroundEffect: normalizeBackgroundEffect(preferences.backgroundEffect ?? mediaPreferencesRef.current.backgroundEffect),
            customBackgroundUrl: preferences.customBackgroundUrl ?? mediaPreferencesRef.current.customBackgroundUrl
        };
    }, []);

    const replaceVideoSenderTrack = useCallback(async nextTrack => {
        await matrixRtcSessionRef.current?.replaceTrack(nextTrack);
    }, []);

    const replaceLocalVideoTrack = useCallback(async nextTrack => {
        const activeStream = localStreamRef.current;
        if (!activeStream) {
            return;
        }
        activeStream.getVideoTracks()
            .filter(track => track.id !== nextTrack.id)
            .forEach(track => {
                activeStream.removeTrack(track);
                if (!track.__meBusinessRetainedForVirtualBackground) {
                    stopTrack(track);
                }
            });
        activeStream.addTrack(nextTrack);
        await replaceVideoSenderTrack(nextTrack);
        setLocalStream(new MediaStream(activeStream.getTracks()));
    }, [replaceVideoSenderTrack]);

    const replaceLocalAudioTrack = useCallback(async nextTrack => {
        const activeStream = localStreamRef.current;
        if (!activeStream) {
            return;
        }
        activeStream.getAudioTracks()
            .filter(track => track.id !== nextTrack.id)
            .forEach(track => {
                activeStream.removeTrack(track);
                track.stop();
            });
        activeStream.addTrack(nextTrack);
        await matrixRtcSessionRef.current?.replaceTrack(nextTrack);
        setLocalStream(new MediaStream(activeStream.getTracks()));
    }, []);

    const stopScreenShare = useCallback(async () => {
        const track = screenShareTrackRef.current;
        if (!track || screenShareStopInProgressRef.current) {
            return true;
        }
        screenShareStopInProgressRef.current = true;
        track.onended = null;
        try {
            await matrixRtcSessionRef.current?.stopScreenShare();
        } finally {
            screenShareTrackRef.current = null;
            setLocalScreenShareStream(null);
            setScreenShareIsActive(false);
            track.stop();
            screenShareStopInProgressRef.current = false;
        }
        return true;
    }, []);

    const stopMedia = useCallback(async () => {
        const session = matrixRtcSessionRef.current;
        matrixRtcSessionRef.current = null;
        await stopScreenShare().catch(() => undefined);
        localStreamRef.current?.getTracks?.().forEach(stopTrack);
        localStreamRef.current = null;
        placeholderVideoTrackRef.current?.stop?.();
        placeholderVideoTrackRef.current = null;
        stopTrack(retainedFirefoxVideoTrackRef.current);
        retainedFirefoxVideoTrackRef.current = null;
        setLocalStream(null);
        setRemoteStreams([]);
        await session?.disconnect?.().catch(error => {
            console.warn("Unable to leave the MatrixRTC call cleanly.", error);
        });
    }, [stopScreenShare]);

    const adoptLobbyStream = useCallback(async (settings = {}) => {
        const { mediaStream: previewStream, ...preferences } = settings;
        setMediaPreferences(preferences);
        let mediaStream = previewStream || new MediaStream();
        if (
            normalizeBackgroundEffect(preferences.backgroundEffect) !== "none"
            && mediaStream.getVideoTracks().some(track => !track.__meBusinessPlaceholder)
        ) {
            try {
                mediaStream = await applyVirtualBackgroundToStream({
                    customBackgroundUrl: preferences.customBackgroundUrl || getSelectedCustomBackgroundUrl(),
                    effect: normalizeBackgroundEffect(preferences.backgroundEffect),
                    stream: mediaStream
                });
            } catch (error) {
                console.warn("Unable to apply the initial virtual background.", error);
                Toast.error("Unable to apply the selected background effect.");
            }
        }
        if (!mediaStream.getVideoTracks().length) {
            placeholderVideoTrackRef.current = createPlaceholderVideoTrack(currentUserInitials());
            mediaStream.addTrack(placeholderVideoTrackRef.current);
        }
        mediaStream.getAudioTracks().forEach(track => {
            track.enabled = preferences.microphoneEnabled !== false;
        });
        localStreamRef.current = mediaStream;
        setLocalStream(mediaStream);
        return preferences;
    }, [currentUserInitials, setMediaPreferences]);

    const prepareRoom = useCallback(async () => {
        if (descriptor.meetingGuest) {
            return;
        }
        if (descriptor.meetingUuid) {
            const joinedMeeting = descriptor.occurrenceUuid
                ? await joinInternalMeetingOccurrence(descriptor.meetingUuid, descriptor.occurrenceUuid)
                : await joinInternalMeeting(descriptor.meetingUuid);
            if (!meetingMatchesCallDescriptor(joinedMeeting, descriptor)) {
                throw new Error("The joined meeting does not match this call.");
            }
            return;
        }
        if (!descriptor.chat?.uuid) {
            throw new Error("The chat is unavailable.");
        }
        try {
            await axios.post(
                `${API_URL}/chats/${descriptor.chat.uuid}/calls/prepare`,
                {},
                bridge.headers
            );
        } catch (error) {
            throw new Error(error.getApiErrorDetails?.().message || "Unable to prepare the chat for calling.");
        }
    }, [
        bridge.headers,
        descriptor.chat?.uuid,
        descriptor.meetingGuest,
        descriptor.meetingUuid,
        descriptor.occurrenceUuid
    ]);

    const connect = useCallback(async (settings = {}) => {
        if (isConnecting || matrixRtcSessionRef.current) {
            return;
        }
        const isMeetingCall = Boolean(descriptor.meetingGuest || descriptor.meetingUuid);
        let matrixRoom = matrixClient?.getRoom?.(descriptor.chat?.matrixRoomId);
        if (!isMeetingCall && !matrixRoom) {
            throw new Error("The Matrix room has not finished loading.");
        }

        setIsConnecting(true);
        await adoptLobbyStream(settings);
        const isOutbound = descriptor.type === "outbound";
        const connectingState = {
            ...callStateRef.current,
            status: isOutbound ? CALL_STATES.OUTBOUND : CALL_STATES.INBOUND
        };
        updateCallState(connectingState);

        try {
            await prepareRoom();
            if (
                isMeetingCall
                && !descriptor.meetingGuest
                && descriptor.chat?.matrixRoomId
                && matrixRoom?.getMyMembership?.() !== "join"
            ) {
                await matrixClient.joinRoom(descriptor.chat.matrixRoomId);
            }
            if (isMeetingCall && descriptor.chat?.matrixRoomId) {
                matrixRoom = await waitForMatrixRtcRoom(matrixClient, descriptor.chat.matrixRoomId);
            } else if (!matrixRoom && descriptor.chat?.matrixRoomId) {
                matrixRoom = await matrixClient.joinRoom(descriptor.chat.matrixRoomId);
            }
            if (!matrixRoom) {
                throw new Error("The Matrix room has not finished loading.");
            }
            const meetingMetadata = descriptor.meetingUuid && descriptor.chat?.originatingMatrixRoomId
                ? createMeetingEventMetadata({
                    meetingUuid: descriptor.meetingUuid,
                    callId: initialCallId,
                    chatUuid: descriptor.chat.uuid,
                    chatRoomId: descriptor.chat.originatingMatrixRoomId,
                    matrixRoomId: descriptor.chat.matrixRoomId
                })
                : null;
            const session = new MatrixRtcLiveKitSession({
                matrixClient,
                matrixRoom,
                meetingMetadata,
                onScreenShareStopped: reason => {
                    if (matrixRtcSessionRef.current !== session) {
                        return;
                    }
                    const track = screenShareTrackRef.current;
                    if (track) {
                        track.onended = null;
                    }
                    screenShareTrackRef.current = null;
                    setLocalScreenShareStream(null);
                    setScreenShareIsActive(false);
                    if (reason) {
                        Toast.info(reason);
                    }
                },
                onParticipantsChanged: streams => {
                    const participants = streams.filter(item => !item.isScreenShare);
                    participants.forEach(item => {
                        if (item.peerUserId) {
                            participantIdsRef.current.add(item.peerUserId);
                        }
                    });
                    setRemoteStreams(streams);
                    if (participants.length > 0) {
                        updateCallState(previous => ({
                            ...previous,
                            status: CALL_STATES.ON_CALL,
                            startedAt: previous.startedAt || Date.now()
                        }));
                    }
                },
                onSessionChanged: matrixSession => {
                    const callId = descriptor.meetingUuid
                        ? initialCallId
                        : session.ringCallId || session.sessionId || matrixSession.callId || initialCallId;
                    updateCallState(previous => ({
                        ...previous,
                        callId,
                        callIds: [callId],
                        groupCallId: callId
                    }));
                }
            });
            session.ringCallId = isOutbound ? initialCallId : "";
            matrixRtcSessionRef.current = session;
            await session.connect({
                localStream: localStreamRef.current,
                ring: isOutbound && !descriptor.meetingUuid
            });
            if (isOutbound && meetingMetadata && descriptor.chat?.originatingMatrixRoomId) {
                const notification = await sendMeetingNotifications(matrixClient, {
                    additionalContent: {
                        meeting_uuid: descriptor.meetingUuid,
                        meeting_room_id: descriptor.chat.matrixRoomId,
                        meeting_url: descriptor.chat.meetingUrl,
                        meeting_host_employee_uuid: descriptor.chat.meetingHostEmployeeUuid
                    },
                    callId: initialCallId,
                    lifetime: MEETING_RING_LIFETIME_MS,
                    meetingMetadata,
                    mode: "ring",
                    recipientIds: getMeetingRecipientIds(
                        descriptor.chat.users,
                        matrixClient.getUserId?.() || ""
                    ),
                    roomId: descriptor.chat.originatingMatrixRoomId,
                    senderTimestamp: Date.now(),
                    summary: { hasNotes: false }
                });
                session.ringEventId = notification.callNotifyEventId || notification.rtcNotificationEventId;
            }
            const connectedCallId = descriptor.meetingUuid
                ? initialCallId
                : session.ringCallId || session.sessionId || initialCallId;
            updateCallState(previous => ({
                ...previous,
                status: isOutbound && session.remoteParticipantCount === 0
                    ? CALL_STATES.OUTBOUND
                    : CALL_STATES.ON_CALL,
                callId: connectedCallId,
                callIds: [connectedCallId],
                groupCallId: connectedCallId,
                eventId: session.ringEventId || previous.eventId,
                startedAt: isOutbound && session.remoteParticipantCount === 0
                    ? 0
                    : previous.startedAt || Date.now()
            }));

        } catch (error) {
            await stopMedia();
            bridge.reportStatus?.({
                ...callStateRef.current,
                status: "failed",
                error: error?.message || "Unable to join the call."
            });
            throw error;
        } finally {
            setIsConnecting(false);
        }
    }, [
        adoptLobbyStream,
        bridge,
        descriptor.chat?.meetingUrl,
        descriptor.chat?.matrixRoomId,
        descriptor.chat?.originatingMatrixRoomId,
        descriptor.meetingUuid,
        descriptor.type,
        initialCallId,
        isConnecting,
        matrixClient,
        prepareRoom,
        stopScreenShare,
        stopMedia,
        updateCallState
    ]);

    const setLocalAudioEnabled = useCallback(async enabled => {
        if (enabled && !localStreamRef.current?.getAudioTracks?.().length) {
            const microphoneDeviceId = normalizeDeviceId(mediaPreferencesRef.current.microphoneDeviceId);
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: microphoneDeviceId ? { deviceId: { exact: microphoneDeviceId } } : true,
                video: false
            });
            const [track] = stream.getAudioTracks();
            if (track) {
                await replaceLocalAudioTrack(track);
            }
        }
        localStreamRef.current?.getAudioTracks?.().forEach(track => {
            track.enabled = enabled;
        });
        setLocalStream(previous => previous ? new MediaStream(previous.getTracks()) : previous);
        matrixRtcSessionRef.current?.setMicrophoneEnabled(enabled).catch(error => {
            console.warn("Unable to update the microphone state.", error);
        });
    }, [replaceLocalAudioTrack]);

    const setLocalMicrophoneDevice = useCallback(async (deviceId = "") => {
        const normalizedDeviceId = normalizeDeviceId(deviceId);
        setMediaPreferences({ microphoneDeviceId: normalizedDeviceId });
        localStorage.setItem("meBusinessCallMicrophone", normalizedDeviceId);
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: normalizedDeviceId ? { deviceId: { exact: normalizedDeviceId } } : true,
            video: false
        });
        const [track] = stream.getAudioTracks();
        if (!track) {
            return false;
        }
        track.enabled = localStreamRef.current?.getAudioTracks?.().some(item => item.enabled) !== false;
        await replaceLocalAudioTrack(track);
        return true;
    }, [replaceLocalAudioTrack, setMediaPreferences]);

    const setLocalVideoEnabled = useCallback(async enabled => {
        const activeStream = localStreamRef.current;
        if (!activeStream) {
            return false;
        }
        if (!enabled) {
            const currentTrack = activeStream.getVideoTracks().find(track => !track.__meBusinessPlaceholder);
            if (isFirefox() && currentTrack) {
                retainedFirefoxVideoTrackRef.current = currentTrack;
                currentTrack.enabled = false;
                placeholderVideoTrackRef.current?.stop?.();
                placeholderVideoTrackRef.current = createPlaceholderVideoTrack(currentUserInitials());
                await replaceVideoSenderTrack(placeholderVideoTrackRef.current);
                const nextStream = new MediaStream([
                    ...activeStream.getAudioTracks(),
                    placeholderVideoTrackRef.current
                ]);
                localStreamRef.current = nextStream;
                setLocalStream(nextStream);
                return true;
            }
            placeholderVideoTrackRef.current?.stop?.();
            placeholderVideoTrackRef.current = createPlaceholderVideoTrack(currentUserInitials());
            await replaceLocalVideoTrack(placeholderVideoTrackRef.current);
            return true;
        }
        let cameraStream = null;
        try {
            const retainedTrack = retainedFirefoxVideoTrackRef.current;
            if (isFirefox() && retainedTrack?.readyState === "live") {
                retainedTrack.enabled = true;
                await replaceLocalVideoTrack(retainedTrack);
                retainedFirefoxVideoTrackRef.current = null;
                return true;
            }

            const cameraDeviceId = normalizeDeviceId(mediaPreferencesRef.current.cameraDeviceId);
            cameraStream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: { ...VIDEO_CONSTRAINTS, ...(cameraDeviceId ? { deviceId: { exact: cameraDeviceId } } : {}) }
            });
            const [cameraTrack] = cameraStream.getVideoTracks();
            if (!cameraTrack) {
                throw new Error("Camera did not return a video track.");
            }
            const effect = mediaPreferencesRef.current.backgroundEffect;
            const nextTrack = effect === "none" ? cameraTrack : await createVirtualBackgroundVideoTrack({
                customBackgroundUrl: mediaPreferencesRef.current.customBackgroundUrl || getSelectedCustomBackgroundUrl(),
                effect,
                sourceTrack: cameraTrack
            }) || cameraTrack;
            await replaceLocalVideoTrack(nextTrack);
            return true;
        } catch (error) {
            cameraStream?.getTracks?.().forEach(track => track.stop());
            if (retainedFirefoxVideoTrackRef.current) {
                retainedFirefoxVideoTrackRef.current.enabled = false;
            }
            console.warn("Unable to start the video feed.", error);
            Toast.error(CAMERA_START_ERROR_MESSAGE);
            return false;
        }
    }, [currentUserInitials, replaceLocalVideoTrack]);

    const setLocalCameraDevice = useCallback(async (deviceId = "") => {
        const normalizedDeviceId = normalizeDeviceId(deviceId);
        setMediaPreferences({ cameraDeviceId: normalizedDeviceId });
        localStorage.setItem("meBusinessCallCamera", normalizedDeviceId);
        if (!localStreamRef.current?.getVideoTracks?.().some(track => !track.__meBusinessPlaceholder)) {
            return true;
        }
        let stream = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: { ...VIDEO_CONSTRAINTS, ...(normalizedDeviceId ? { deviceId: { exact: normalizedDeviceId } } : {}) }
            });
            const [track] = stream.getVideoTracks();
            if (!track) {
                throw new Error("Camera did not return a video track.");
            }
            const effect = mediaPreferencesRef.current.backgroundEffect;
            const nextTrack = effect === "none" ? track : await createVirtualBackgroundVideoTrack({
                customBackgroundUrl: mediaPreferencesRef.current.customBackgroundUrl || getSelectedCustomBackgroundUrl(),
                effect,
                sourceTrack: track
            }) || track;
            await replaceLocalVideoTrack(nextTrack);
            return true;
        } catch (error) {
            stream?.getTracks?.().forEach(track => track.stop());
            console.warn("Unable to change the camera.", error);
            Toast.error(CAMERA_START_ERROR_MESSAGE);
            return false;
        }
    }, [replaceLocalVideoTrack, setMediaPreferences]);

    const setLocalSpeakerDevice = useCallback((deviceId = "") => {
        setMediaPreferences({ speakerDeviceId: deviceId });
        localStorage.setItem("meBusinessCallSpeaker", deviceId);
        return true;
    }, [setMediaPreferences]);

    const setLocalBackgroundEffect = useCallback(async (effect, options = {}) => {
        const normalizedEffect = normalizeBackgroundEffect(effect);
        const customBackgroundUrl = options.customBackgroundUrl ?? getSelectedCustomBackgroundUrl();
        setMediaPreferences({ backgroundEffect: normalizedEffect, customBackgroundUrl });
        localStorage.setItem("meBusinessCallBackground", normalizedEffect);
        const currentTrack = localStreamRef.current?.getVideoTracks?.().find(track => !track.__meBusinessPlaceholder);
        if (!currentTrack) {
            return true;
        }
        if (currentTrack.__meBusinessSetVirtualBackground) {
            await currentTrack.__meBusinessSetVirtualBackground({ effect: normalizedEffect, customBackgroundUrl });
            return true;
        }
        const nextTrack = normalizedEffect === "none"
            ? currentTrack
            : await createVirtualBackgroundVideoTrack({
                customBackgroundUrl,
                effect: normalizedEffect,
                sourceTrack: currentTrack
            }) || currentTrack;
        if (nextTrack !== currentTrack) {
            currentTrack.__meBusinessRetainedForVirtualBackground = true;
            await replaceLocalVideoTrack(nextTrack);
        }
        return true;
    }, [replaceLocalVideoTrack, setMediaPreferences]);

    const toggleScreenShare = useCallback(async gestureWindow => {
        if (screenShareStartInProgressRef.current) {
            return false;
        }
        if (screenShareTrackRef.current) {
            return stopScreenShare();
        }
        const session = matrixRtcSessionRef.current;
        if (!session) {
            return false;
        }
        const captureWindow = gestureWindow || window;
        screenShareStartInProgressRef.current = true;
        let stream = null;
        try {
            stream = await captureWindow.navigator.mediaDevices.getDisplayMedia({ video: true });
            const [track] = stream.getVideoTracks();
            if (!track || matrixRtcSessionRef.current !== session) {
                throw new Error("The call changed while preparing screen sharing.");
            }
            screenShareTrackRef.current = track;
            await session.startScreenShare(track);
            if (matrixRtcSessionRef.current !== session || track.readyState !== "live") {
                throw new Error("Screen sharing ended before it was ready.");
            }
            setLocalScreenShareStream(stream);
            setScreenShareIsActive(true);
            track.onended = () => {
                if (matrixRtcSessionRef.current === session && screenShareTrackRef.current === track) {
                    stopScreenShare().catch(error => console.warn("Unable to stop ended screen sharing.", error));
                }
            };
            return true;
        } catch (error) {
            if (stream && session.screenShareCapture === stream.getVideoTracks()[0]) {
                await session.stopScreenShare().catch(stopError => {
                    console.warn("Unable to clean up failed screen sharing.", stopError);
                });
            }
            if (matrixRtcSessionRef.current === session) {
                screenShareTrackRef.current = null;
                setLocalScreenShareStream(null);
                setScreenShareIsActive(false);
            }
            stream?.getTracks().forEach(track => track.stop());
            console.warn("Unable to start screen sharing.", error);
            if (error?.name !== "NotAllowedError" && error?.name !== "AbortError") {
                Toast.error(SCREEN_SHARE_FAILURE_MESSAGE);
            }
            return false;
        } finally {
            screenShareStartInProgressRef.current = false;
        }
    }, [stopScreenShare]);

    const notifyTerminalMeeting = useCallback(async (finalState, participantUserIds, sessionSnapshot) => {
        if (!descriptor.meetingUuid || !descriptor.chat?.originatingMatrixRoomId) {
            return;
        }

        const endedAt = Date.now();
        const callId = finalState.callId || initialCallId;
        const membershipParticipantIds = (sessionSnapshot.memberships || [])
            .map(membership => membership.sender)
            .filter(Boolean);
        const allParticipantIds = Array.from(new Set([
            ...participantUserIds,
            ...membershipParticipantIds,
            matrixClient.getUserId?.() || ""
        ].filter(Boolean)));
        const meetingMetadata = createMeetingEventMetadata({
            meetingUuid: descriptor.meetingUuid,
            callId,
            chatUuid: descriptor.chat.uuid,
            chatRoomId: descriptor.chat.originatingMatrixRoomId,
            matrixRoomId: descriptor.chat.matrixRoomId
        });
        const sharedSummary = finalState.startedAt
            ? createSharedCallSummary({
                startedAt: finalState.startedAt,
                ...(sessionSnapshot.remoteParticipantCount > 0 ? {} : { endedAt }),
                expiresAt: getLatestMembershipExpiry(sessionSnapshot.memberships, endedAt),
                participantIds: allParticipantIds,
                initiatorId: finalState.direction === "outbound"
                    ? matrixClient.getUserId?.() || ""
                    : finalState.sender || matrixClient.getUserId?.() || "",
                ...(typeof hasNotesRef.current === "boolean" ? { hasNotes: hasNotesRef.current } : {})
            })
            : typeof hasNotesRef.current === "boolean"
                ? { hasNotes: hasNotesRef.current }
                : undefined;

        await sendMeetingNotifications(matrixClient, {
            additionalContent: {
                meeting_uuid: descriptor.meetingUuid,
                meeting_room_id: descriptor.chat.matrixRoomId
            },
            callId,
            lifetime: 0,
            meetingMetadata,
            mode: "notification",
            recipientIds: [],
            roomId: descriptor.chat.originatingMatrixRoomId,
            senderTimestamp: endedAt,
            summary: sharedSummary
        });
    }, [
        descriptor.chat?.matrixRoomId,
        descriptor.chat?.originatingMatrixRoomId,
        descriptor.chat?.uuid,
        descriptor.meetingUuid,
        initialCallId,
        matrixClient
    ]);

    const publishMeetingCallSummary = useCallback(async (finalState, action, participantUserIds) => {
        const summaryRoomId = descriptor.chat?.originatingMatrixRoomId;
        const isCompletedCall = finalState.status === CALL_STATES.ON_CALL && Boolean(finalState.startedAt);
        const isDeclinedCall = finalState.status === CALL_STATES.INBOUND && ["cancel", "reject"].includes(action);
        const isCancelledCall = finalState.status === CALL_STATES.OUTBOUND && action === "cancel";
        if (
            !descriptor.meetingUuid
            || !summaryRoomId
            || (!isCompletedCall && !isDeclinedCall && !isCancelledCall)
            || (isCompletedCall && !isMeetingSummaryPublisher)
        ) {
            return false;
        }

        const durationSeconds = isCompletedCall
            ? Math.max(1, Math.round((Date.now() - finalState.startedAt) / 1000))
            : 0;
        const outcome = isDeclinedCall ? "declined" : isCancelledCall ? "cancelled" : "completed";
        const endedAt = Date.now();
        const meetingMetadata = createMeetingEventMetadata({
            meetingUuid: descriptor.meetingUuid,
            callId: finalState.callId,
            chatUuid: descriptor.chat.uuid,
            chatRoomId: summaryRoomId,
            matrixRoomId: descriptor.chat.matrixRoomId
        });
        const sharedSummary = isCompletedCall
            ? createSharedCallSummary({
                startedAt: finalState.startedAt,
                ...(matrixRtcSessionRef.current?.remoteParticipantCount > 0 ? {} : { endedAt }),
                expiresAt: getLatestMembershipExpiry(matrixRtcSessionRef.current?.memberships, endedAt),
                participantIds: participantUserIds,
                initiatorId: finalState.direction === "outbound"
                    ? matrixClient.getUserId?.() || ""
                    : finalState.sender || matrixClient.getUserId?.() || "",
                ...(typeof hasNotesRef.current === "boolean" ? { hasNotes: hasNotesRef.current } : {})
            })
            : typeof hasNotesRef.current === "boolean"
                ? { hasNotes: hasNotesRef.current }
                : null;
        try {
            await matrixClient.sendEvent(summaryRoomId, "m.room.message", {
                msgtype: "m.notice",
                body: `Call ended after ${durationSeconds} seconds.`,
                [MEETING_EVENT_METADATA_FIELD]: meetingMetadata,
                ...(sharedSummary ? { [SHARED_CALL_SUMMARY_FIELD]: sharedSummary } : {}),
                [WEB_CALL_SUMMARY_FIELD]: {
                    call_id: finalState.callId,
                    duration_seconds: durationSeconds,
                    participant_user_ids: participantUserIds,
                    ended_reason: action,
                    ended_by_user_id: matrixClient.getUserId?.() || "",
                    outcome,
                    is_group_call: Boolean(finalState.isGroupCall)
                },
                ...(finalState.eventId ? {
                    "m.relates_to": {
                        rel_type: "m.reference",
                        event_id: finalState.eventId
                    }
                } : {})
            });
            return true;
        } catch (error) {
            console.warn("Unable to publish the meeting call summary from the call window.", error);
            return false;
        }
    }, [
        descriptor.chat?.originatingMatrixRoomId,
        descriptor.chat?.matrixRoomId,
        descriptor.chat?.uuid,
        descriptor.meetingUuid,
        isMeetingSummaryPublisher,
        matrixClient
    ]);

    const finish = useCallback(async (action = "hangup") => {
        if (closingRef.current) {
            return;
        }
        closingRef.current = true;
        const finalState = callStateRef.current;
        const participantUserIds = Array.from(participantIdsRef.current);
        const session = matrixRtcSessionRef.current;
        const sessionSnapshot = {
            memberships: [...(session?.memberships || [])],
            remoteParticipantCount: session?.remoteParticipantCount || 0
        };
        const callStillActive = sessionSnapshot.remoteParticipantCount > 0;
        const summaryWasPublished = callStillActive
            ? false
            : await publishMeetingCallSummary(finalState, action, participantUserIds);
        const publishSummary = callStillActive
            ? false
            : descriptor.meetingUuid
                ? !summaryWasPublished
                : !matrixRtcSessionRef.current || matrixRtcSessionRef.current.memberships.length <= 1;
        await stopMedia();
        if (descriptor.meetingUuid) {
            const leaveMeeting = descriptor.occurrenceUuid
                ? leaveInternalMeetingOccurrence(descriptor.meetingUuid, descriptor.occurrenceUuid)
                : leaveInternalMeeting(descriptor.meetingUuid);
            await leaveMeeting.catch(error => {
                console.warn("Unable to leave the meeting through the API.", error);
            });
        }
        if (!callStillActive) {
            await notifyTerminalMeeting(finalState, participantUserIds, sessionSnapshot).catch(error => {
                console.warn("Unable to publish the meeting history notification.", error);
            });
        }
        bridge.reportStatus?.({
            ...finalState,
            status: "ended",
            action,
            callStillActive,
            publishSummary,
            participantUserIds
        });
        bridge.removeCall?.(callToken);
        window.close();
    }, [
        bridge,
        callToken,
        descriptor.meetingUuid,
        descriptor.occurrenceUuid,
        isMeetingSummaryPublisher,
        notifyTerminalMeeting,
        publishMeetingCallSummary,
        stopMedia
    ]);

    useEffect(() => {
        if (!descriptor.meetingUuid || descriptor.meetingGuest) {
            return undefined;
        }
        const checkMeetingStatus = async () => {
            if (!matrixRtcSessionRef.current || closingRef.current) {
                return;
            }
            try {
                const status = descriptor.occurrenceUuid
                    ? await getMeetingOccurrence(descriptor.meetingUuid, descriptor.occurrenceUuid)
                    : await getMeeting(descriptor.meetingUuid);
                if (status.status !== "active") {
                    await finish("ended_by_host");
                }
            } catch {
                // A temporary status request failure must not interrupt an active call.
            }
        };
        const intervalId = window.setInterval(checkMeetingStatus, 2000);
        return () => window.clearInterval(intervalId);
    }, [descriptor.meetingGuest, descriptor.meetingUuid, descriptor.occurrenceUuid, finish]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (!closingRef.current) {
                const finalState = callStateRef.current;
                const action = finalState.status === CALL_STATES.INBOUND
                    ? "reject"
                    : finalState.status === CALL_STATES.IDLE ? "cancel" : "hangup";
                const session = matrixRtcSessionRef.current;
                const callStillActive = (session?.remoteParticipantCount || 0) > 0;
                if (!callStillActive) {
                    notifyTerminalMeeting(
                        finalState,
                        Array.from(participantIdsRef.current),
                        {
                            memberships: [...(session?.memberships || [])],
                            remoteParticipantCount: session?.remoteParticipantCount || 0
                        }
                    ).catch(() => undefined);
                }
                if (descriptor.meetingUuid && !descriptor.meetingGuest) {
                    leaveInternalMeeting(descriptor.meetingUuid).catch(() => undefined);
                }
                const publishSummary = callStillActive
                    ? false
                    : descriptor.meetingUuid
                        ? true
                        : !matrixRtcSessionRef.current || matrixRtcSessionRef.current.memberships.length <= 1;
                stopMedia();
                bridge.reportStatus?.({
                    ...callStateRef.current,
                    status: "ended",
                    action,
                    callStillActive,
                    publishSummary,
                    participantUserIds: Array.from(participantIdsRef.current)
                });
                bridge.removeCall?.(callToken);
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [
        bridge,
        callToken,
        descriptor.meetingUuid,
        descriptor.meetingGuest,
        isMeetingSummaryPublisher,
        notifyTerminalMeeting,
        stopMedia
    ]);

    const cancel = useCallback(
        () => finish(descriptor.type === "inbound" ? "reject" : "cancel"),
        [descriptor.type, finish]
    );
    const hangup = useCallback(() => finish("hangup"), [finish]);

    return {
        callState,
        isConnecting,
        markCallHasNotes,
        localScreenShareStream,
        localStream,
        remoteStreams,
        screenShareIsActive,
        connect,
        cancel,
        hangup,
        setLocalAudioEnabled,
        setLocalMicrophoneDevice,
        setLocalVideoEnabled,
        setLocalCameraDevice,
        setLocalSpeakerDevice,
        setLocalBackgroundEffect,
        toggleScreenShare
    };
};
